import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModuleStore } from '@/stores/module-store';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { getUserDetailsByWebhookToken } from '@/lib/firebase/functions';
import { decodeWebhookUrl, extractWebhookToken } from '@/lib/webhook-url';
import { EVENT_APPS, WEBHOOK_SERVER_BASE_URL } from '@/config/constants';
import type { WebhookSubscription } from '@/types/tpa';

export interface EventWebhookOwnership {
  /** True when an existing webhook for this app/org belongs to another user. */
  ownedByOther: boolean;
  /** Resolved name/email of the user who configured the existing webhook. */
  configurer: { name: string; email: string } | null;
  /** True while a takeover (delete-then-create) is in flight. */
  becomingAdmin: boolean;
  /**
   * Delete the previous admin's webhook(s) and recreate one under the current
   * user so future events use their configuration.
   */
  becomeAdmin: () => Promise<void>;
  /** The current user's authorized webhook URL (includes their token). */
  currentWebhookUrl: string | null;
}

/**
 * Detects whether an incoming-sync webhook for the current event app/org was
 * configured by a *different* org user, and exposes a "take over as admin"
 * action. Ownership is decided by the full per-user token in the webhook URL
 * — every org user shares the `?a=ext&o=orgId` base but carries a unique
 * `&t=token`, so a base match without a token match means someone else owns it.
 *
 * Self-contained: fetches its own webhook list. Mirrors the inline logic in
 * `event-incoming-sync-settings.tsx` so the field-mapping page can show the
 * same takeover alert.
 */
export function useEventWebhookOwnership(): EventWebhookOwnership {
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const webhookToken = useModuleStore((s) => s.webhookToken);
  const fetchWebhookToken = useModuleStore((s) => s.fetchWebhookToken);

  const extensionName = useAppStore((s) => s.extensionName);
  const appConfig = useAppStore((s) => s.appConfig);
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const user = useAuthStore((s) => s.user);

  const isEventApp = appCodeTPA != null && (EVENT_APPS as readonly string[]).includes(appCodeTPA);

  const [webhooks, setWebhooks] = useState<WebhookSubscription[]>([]);
  const [configurer, setConfigurer] = useState<{ name: string; email: string } | null>(null);
  const [becomingAdmin, setBecomingAdmin] = useState(false);

  const fetchWebhooks = useCallback(async () => {
    if (!tpaModule || !tpaModule.getWebhooks) return;
    try {
      const response = await tpaModule.getWebhooks();
      if (response?.success && response.data) {
        setWebhooks(response.data);
      }
    } catch (error) {
      console.error('[useEventWebhookOwnership] fetchWebhooks failed:', error);
    }
  }, [tpaModule]);

  // Ensure a webhook token exists so we can build the current user's URL.
  useEffect(() => {
    if (isEventApp && tpaAuthenticated && !webhookToken && extensionName) {
      fetchWebhookToken(extensionName);
    }
  }, [isEventApp, tpaAuthenticated, webhookToken, extensionName, fetchWebhookToken]);

  // Fetch the webhook list when authenticated.
  useEffect(() => {
    if (isEventApp && tpaAuthenticated && tpaModule) {
      fetchWebhooks();
    }
  }, [isEventApp, tpaAuthenticated, tpaModule, fetchWebhooks]);

  const currentWebhookUrl = useMemo(() => {
    if (!tpaModule || !extensionName || !appConfig?.orgId) return null;
    const baseUrl = WEBHOOK_SERVER_BASE_URL;
    const orgId = appConfig.orgId;
    const token = webhookToken?.tpa || '';
    return `${baseUrl}?a=${extensionName}&o=${orgId}${token ? `&t=${token}` : ''}`;
  }, [tpaModule, extensionName, appConfig?.orgId, webhookToken]);

  const tokenReady = !!webhookToken?.tpa;
  const webhookBaseUrl = useMemo(
    () => (currentWebhookUrl ? currentWebhookUrl.split('&t=')[0] : null),
    [currentWebhookUrl]
  );

  const relevantWebhooks = useMemo(() => {
    if (!webhookBaseUrl) return [];
    return webhooks.filter((w) => w.url?.startsWith(webhookBaseUrl));
  }, [webhooks, webhookBaseUrl]);

  // Both sides are decoded first: the stored token may be percent-encoded
  // while the one we build is raw, so the strings differ even when the token
  // is identical.
  const decodedCurrentUrl = useMemo(
    () => decodeWebhookUrl(currentWebhookUrl),
    [currentWebhookUrl]
  );

  const otherAdminWebhooks = useMemo(
    () => relevantWebhooks.filter((w) => decodeWebhookUrl(w.url) !== decodedCurrentUrl),
    [relevantWebhooks, decodedCurrentUrl]
  );

  const myWebhook = useMemo(
    () =>
      (decodedCurrentUrl &&
        relevantWebhooks.find((w) => decodeWebhookUrl(w.url) === decodedCurrentUrl)) ||
      null,
    [relevantWebhooks, decodedCurrentUrl]
  );

  // URL mismatch alone can't tell WHO owns the webhook — after the Acuity
  // short-token switch (KAN-85) the user's own legacy-token webhook no longer
  // string-matches — so the token is resolved to its owner below and a
  // self-match cancels the takeover state.
  const webhookUrlMismatch = isEventApp && tokenReady && !myWebhook && otherAdminWebhooks.length > 0;

  // Resolve the configurer's name/email from the other webhooks' tokens.
  // EVERY distinct token is resolved, not just [0]: with mixed ownership the
  // verdict must not depend on the provider's list order — any foreign owner
  // must block editing.
  useEffect(() => {
    if (!webhookUrlMismatch || !extensionName) {
      setConfigurer(null);
      return;
    }
    const tokens = Array.from(
      new Set(
        otherAdminWebhooks
          .map((w) => extractWebhookToken(w.url))
          .filter((t): t is string => !!t)
      )
    );
    if (tokens.length === 0) {
      setConfigurer(null);
      return;
    }
    let active = true;
    Promise.all(
      tokens.map((t) =>
        getUserDetailsByWebhookToken(extensionName, t, 'tpa').catch(() => null)
      )
    ).then((results) => {
      if (!active) return;
      const resolved = results.filter(
        (r): r is { name: string; email: string } => !!r
      );
      const myEmail = user?.email?.toLowerCase();
      const foreign = resolved.find(
        (r) => !myEmail || r.email?.toLowerCase() !== myEmail
      );
      setConfigurer(foreign || resolved[0] || null);
    });
    return () => {
      active = false;
    };
  }, [webhookUrlMismatch, otherAdminWebhooks, extensionName, user?.email]);

  // A webhook registered with the current user's own (legacy) token is still
  // THEIR webhook — only a different resolved owner counts as a takeover.
  const isSelfConfigurer = !!(
    configurer?.email &&
    user?.email &&
    configurer.email.toLowerCase() === user.email.toLowerCase()
  );
  const ownedByOther = webhookUrlMismatch && !isSelfConfigurer;

  const becomeAdmin = useCallback(async () => {
    if (!tpaModule || !extensionName) return;
    setBecomingAdmin(true);
    try {
      if (!webhookToken) {
        await fetchWebhookToken(extensionName);
      }
      const token = useModuleStore.getState().webhookToken;
      if (!token?.tpa) {
        throw new Error('Could not resolve your webhook token');
      }
      for (const wh of otherAdminWebhooks) {
        await tpaModule.deleteWebhook?.(wh.id);
      }
      await tpaModule.createWebhook({ token: token.tpa });
      await fetchWebhooks();
      await useSettingsStore.getState().updateEventIncomingCapture({ enabled: true });
    } finally {
      setBecomingAdmin(false);
    }
  }, [tpaModule, extensionName, webhookToken, fetchWebhookToken, otherAdminWebhooks, fetchWebhooks]);

  return { ownedByOther, configurer, becomingAdmin, becomeAdmin, currentWebhookUrl };
}
